import { Formik, FormikHelpers } from "formik";
import { memo, useContext } from "react";
import DatePicker from "react-date-picker";
import Select from "react-select";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import { addDays, convertDateToApiFormat } from "../../../app-constants";
import { colourStyles } from "../../../business-travel/config/bt-constants";
import { CorporateGroupTravelContext } from "../../CorporateGroupTravel";
import cgConstants, { travelInsuranceInstructions, usStates } from "../../config/cgConstants";
import { ICorporateGroupTravelContext } from "../../config/interfaces";
import CGQuoteApi from "../api/CGQuoteApi";
import CheckCircleIcon from "../icons/CheckCircleIcon";

const validationSchema = yup.object().shape({
  companyLocation: yup.object().required("This field is required"),
  startDate: yup.date().required(),
  endDate: yup.date().required(),
  destinationCountries: yup.array().min(1, "At least one destination country is required"),
  numberOfTravellers: yup
    .number()
    .min(1, "This field is required")
    .required("This field is required")
    .max(1000, "Travellers should not more than 1000"),
});

const CGQuoteForm = () => {
  const { quoteData, setQuoteData, hasError, setHasError, destinationCountriesList, setQuoteResult, next } = useContext(
    CorporateGroupTravelContext
  ) as ICorporateGroupTravelContext;

  const initialValues = {
    companyLocation: quoteData?.companyLocation || null,
    startDate: quoteData?.startDate || null,
    endDate: quoteData?.endDate || null,
    destinationCountries: quoteData?.destinationCountries || null,
    numberOfTravellers: quoteData?.numberOfTravellers || 0,
  };

  const handleFormSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
    try {
      setHasError(null);
      let _quoteData = { ...quoteData };
      setSubmitting(true);
      _quoteData.companyLocation = values.companyLocation;
      _quoteData.startDate = values.startDate;
      _quoteData.endDate = values.endDate;
      _quoteData.destinationCountries = values.destinationCountries;
      _quoteData.numberOfTravellers = values.numberOfTravellers;

      const data = {
        start_date: convertDateToApiFormat(values.startDate),
        end_date: convertDateToApiFormat(values.endDate),
        currency: quoteData?.currency,
        destinations: values.destinationCountries?.map((item: any) => item.code),
        applicants: quoteData?.applicants,
        packages: [
          {
            id: "base_package",
            group_details: [
              {
                type: "travelers",
                amount: values.numberOfTravellers,
              },
            ],
          },
        ],
        product: quoteData?.productId,
        host_country: quoteData?.hostCountry,
        host_country_state: values.companyLocation?.code,
      };

      const createQuoteResponse = await CGQuoteApi.createQuote(data);
      if (createQuoteResponse.errors) {
        throw createQuoteResponse;
      }

      setQuoteResult(createQuoteResponse?.data);
      setQuoteData(_quoteData);
      next();
    } catch (e: any) {
      setHasError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={appStyles.CGTCoverageListWarp}>
        <ul>
          {travelInsuranceInstructions?.map((instruction: string, index: number) => (
            <li key={index}>
              <CheckCircleIcon /> {instruction}
            </li>
          ))}
        </ul>
      </div>

      <div className={`${appStyles.CGTBasicForm}`}>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit, validateForm, isSubmitting }) => (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await validateForm();
                handleSubmit();
              }}
            >
              <div className={appStyles.formWrapper}>
                <div className={appStyles.formRow}>
                  <div className={appStyles.formBlock}>
                    <label className={appStyles.CGTBasicFormLabel}>Company Location</label>

                    <div
                      className={`${appStyles.formField} ${
                        touched?.companyLocation && errors?.companyLocation
                          ? `${appStyles.hasError} has-error-select`
                          : ""
                      }`}
                    >
                      <Select
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        styles={colourStyles}
                        isSearchable={true}
                        name="companyLocation"
                        id="companyLocation"
                        value={values.companyLocation}
                        options={usStates || []}
                        placeholder="Select State"
                        onBlur={handleBlur("companyLocation")}
                        onChange={(_state: any) => {
                          setFieldValue("companyLocation", _state);
                        }}
                        getOptionValue={(option: any) => option?.code}
                        getOptionLabel={(option: any) => option?.name}
                        blurInputOnSelect={false}
                        classNamePrefix="CGTBasicFormSelect"
                      />

                      {touched?.companyLocation && errors?.companyLocation && (
                        <div className={`${appStyles.formError} `}>This field is required</div>
                      )}
                    </div>

                    <p className={appStyles.CGTBasicFormCompanyNote}>
                      Not available to companies in MN, MO, MT, NY, OR, or WA
                    </p>
                  </div>
                </div>

                {/* start Date end date */}
                <div className={appStyles.formRow}>
                  <div className={appStyles.formBlock}>
                    <div
                      className={`${appStyles.formField} ${
                        touched?.startDate && errors?.startDate ? `${appStyles.hasError} has-error-date-picker` : ""
                      }`}
                    >
                      <label className={appStyles.btFormLabel}>Trip Start Date</label>
                      <DatePicker
                        calendarClassName={`${appStyles.bfpDatePickerCalendar} ${appStyles.btDatePickerCalendar}`}
                        className={`cgt-datepicker-warpper ${appStyles.bfpDatePicker} ${appStyles.btDatePicker} ${appStyles.bfpDatePickerCalendarStartDate} ${appStyles.btDatePickerCalendarStartDate}`}
                        value={values.startDate}
                        id="startDate"
                        format={cgConstants.CGDatePickerFormat}
                        onChange={(value: any) => {
                          setFieldValue("startDate", value);
                          if (!values?.endDate || value > values.endDate) {
                            setFieldValue("endDate", value);
                          }
                        }}
                        onKeyPress={(e) => e.preventDefault()}
                        onBlur={handleBlur("startDate")}
                        minDate={addDays(new Date(), 1)}
                        maxDate={addDays(new Date(), 730)}
                      />
                      {touched?.startDate && errors?.startDate && (
                        <div className={appStyles.formError}>This field is required</div>
                      )}
                    </div>
                  </div>

                  <div className={appStyles.formBlock}>
                    <div
                      className={`${appStyles.formField} ${
                        touched?.endDate && errors?.endDate ? `${appStyles.hasError} has-error-date-picker` : ""
                      }`}
                    >
                      <label className={appStyles.btFormLabel}>Trip End Date</label>
                      <DatePicker
                        calendarClassName={`${appStyles.bfpDatePickerCalendar} ${appStyles.btDatePickerCalendar}`}
                        className={`cgt-datepicker-warpper ${appStyles.bfpDatePicker} ${appStyles.btDatePicker} ${appStyles.bfpDatePickerCalendarStartDate} ${appStyles.btDatePickerCalendarStartDate}`}
                        value={values.endDate}
                        id="endDate"
                        format={cgConstants.CGDatePickerFormat}
                        onChange={(value: any) => {
                          setFieldValue("endDate", value);
                        }}
                        onKeyPress={(e) => e.preventDefault()}
                        onBlur={handleBlur("endDate")}
                        minDate={values?.startDate || addDays(new Date(), 1)}
                        maxDate={addDays(values?.startDate || new Date(), 20)}
                      />
                      {touched?.endDate && errors?.endDate && (
                        <div className={appStyles.formError}>This field is required</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* destination countries multi */}
                <div className={appStyles.formRow}>
                  <div className={appStyles.formBlock}>
                    <label className={appStyles.btFormLabel}>Destination Countries</label>

                    <div
                      className={`${appStyles.formField} ${
                        touched?.destinationCountries && errors?.destinationCountries
                          ? `${appStyles.hasError} has-error-select`
                          : ""
                      }`}
                    >
                      <Select
                        components={{
                          DropdownIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        styles={colourStyles}
                        isSearchable={true}
                        name="destinationCountries"
                        id="destinationCountries"
                        isMulti={true}
                        value={values.destinationCountries}
                        options={destinationCountriesList || []}
                        isLoading={!destinationCountriesList ? true : false}
                        onBlur={handleBlur("destinationCountries")}
                        onChange={(_countries: any) => {
                          setFieldValue("destinationCountries", _countries);
                        }}
                        getOptionValue={(option: any) => `${option?.code}`}
                        getOptionLabel={(option: any) => `${option?.name}`}
                        className={`${appStyles.btSearchSelect}`}
                        classNamePrefix="CGTBasicFormSelect"
                        blurInputOnSelect={false}
                      />
                      {touched?.destinationCountries && errors?.destinationCountries && (
                        <div className={appStyles.formError}>This field is required</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={appStyles.formRow}>
                  <div className={appStyles.formBlock}>
                    <div
                      className={`${appStyles.formField} ${
                        (touched?.numberOfTravellers && errors?.numberOfTravellers) || values.numberOfTravellers > 1000
                          ? appStyles.hasError
                          : ""
                      }`}
                    >
                      <label className={appStyles.btFormLabel}>Number of Travelers</label>
                      <input
                        type={"number"}
                        name={"numberOfTravellers"}
                        id={"numberOfTravellers"}
                        value={`${values?.numberOfTravellers || ""}`}
                        className={`${appStyles.bfFormControl} ${appStyles.bfFormControlSimple} ${appStyles.btInput} ${appStyles.btPlaceholder}`}
                        onChange={(event: any) => {
                          const inputValue = parseInt(event.target.value, 10) || "";
                          setFieldValue("numberOfTravellers", inputValue);
                        }}
                        min={1}
                        max={1000}
                        onBlur={handleBlur}
                      />
                      {touched?.numberOfTravellers && errors?.numberOfTravellers && (
                        <div className={appStyles.formError}>{errors?.numberOfTravellers}</div>
                      )}
                    </div>
                    <span className={`${appStyles.ageWarning} ${appStyles.mt12}`}>
                      The company purchasing the plan must be domiciled in the US.
                    </span>
                    <span className={appStyles.ageWarning}>
                      (Maximum age for any traveler is 70 at time of purchase.)
                    </span>
                  </div>
                </div>
              </div>

              <div className={appStyles.formRow}>
                <div className={`${appStyles.buttonBlock}`}>
                  <button
                    className={`${appStyles.CGTBasicFormBtn}  ${appStyles.btButton100}`}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {!isSubmitting ? "Get a Quote" : "Getting a Quote..."}
                  </button>
                </div>
              </div>
              {hasError && (
                <div className={appStyles.formRow}>
                  <div className={appStyles.battlefaceServerError}>{hasError}</div>
                </div>
              )}
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default memo(CGQuoteForm);
